import React from "react";
import MainLayout from "../../layouts/main/main_layout";
import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import MiscellaneousServicesRoundedIcon from "@mui/icons-material/MiscellaneousServicesRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import {
  amber,
  amberLight,
  black,
  blue,
  blueLight,
  green,
  greenLight,
  greyEnabled,
  indigo,
  indigoLight,
  orange,
  orangeLightActive,
  purple,
  purpleLight,
  teal,
  tealLight,
} from "../../theme/colors";

import {
  admin_report,
  grievance_report,
  jobs_reports,
  respondent_report,
  rewards_reports,
  schemes_report,
  service_report,
} from "../../config";

interface ReportItem {
  title: string;
  description: string;
  link: string;
  icon: SvgIconComponent;
  accent: string;
  accentTint: string;
}

export default function Reports() {
  let reports: ReportItem[] = [
    {
      title: "Admin Report",
      description: "Platform-wide activity, user counts, and system health at a glance.",
      link: admin_report,
      icon: AdminPanelSettingsRoundedIcon,
      accent: purple,
      accentTint: purpleLight,
    },
    {
      title: "Respondent Report",
      description: "Demographics and response trends captured from field respondents.",
      link: respondent_report,
      icon: GroupsRoundedIcon,
      accent: blue,
      accentTint: blueLight,
    },
    {
      title: "Schemes Report",
      description: "Enrollment numbers and performance across government schemes.",
      link: schemes_report,
      icon: AccountBalanceRoundedIcon,
      accent: green,
      accentTint: greenLight,
    },
    {
      title: "Grievance Report",
      description: "Status and resolution tracking for citizen-raised grievances.",
      link: grievance_report,
      icon: ReportProblemRoundedIcon,
      accent: orange,
      accentTint: orangeLightActive,
    },
    {
      title: "Service Report",
      description: "Request volumes, turnaround time, and service delivery metrics.",
      link: service_report,
      icon: MiscellaneousServicesRoundedIcon,
      accent: teal,
      accentTint: tealLight,
    },
    {
      title: "Jobs Report",
      description: "Job postings, applications received, and placement outcomes.",
      link: jobs_reports,
      icon: WorkRoundedIcon,
      accent: indigo,
      accentTint: indigoLight,
    },
    {
      title: "Rewards Report",
      description: "Reward points issued, redeemed, and outstanding by user.",
      link: rewards_reports,
      icon: EmojiEventsRoundedIcon,
      accent: amber,
      accentTint: amberLight,
    },
  ];
  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={700} color={black}>
          Reports
        </Typography>
        <Typography variant="body2" color={greyEnabled} mt={0.5}>
          Select a report below to open its live dashboard in a new tab.
        </Typography>

        <Grid container spacing={3} mt={0.5}>
          {reports?.map((reportItem) => {
            const Icon = reportItem.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={reportItem.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    p: 3,
                    pt: 2.5,
                    borderRadius: 3,
                    border: "1px solid #ECECEC",
                    transition: "box-shadow 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 16px 32px -16px rgba(27, 0, 27, 0.25)",
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      bgcolor: reportItem.accentTint,
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: reportItem.accent, fontSize: 26 }} />
                  </Box>

                  <Typography variant="subtitle1" fontWeight={700}>
                    {reportItem.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color={greyEnabled}
                    sx={{ flex: 1 }}
                  >
                    {reportItem.description}
                  </Typography>

                  <Button
                    component="a"
                    href={reportItem.link}
                    target="_blank"
                    rel="noreferrer"
                    variant="contained"
                    disableElevation
                    endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                    sx={{
                      mt: 1,
                      alignSelf: "flex-start",
                      bgcolor: purple,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: "#7A2074" },
                    }}
                  >
                    View Report
                  </Button>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </MainLayout>
  );
}
